import { forwardRef, useId, type ReactNode, type SVGProps } from 'react';

export interface IconProps extends SVGProps<SVGSVGElement> {
  size?: number | string;
  title?: string;
}

/** Color Match / Chip: a 24-unit grid, clipped frames and solid color samples. */
function createIcon(name: string, geometry: ReactNode) {
  const Icon = forwardRef<SVGSVGElement, IconProps>(function ChipIcon(
    { size = 24, title, className = '', children, ...props }, ref,
  ) {
    const titleId = useId();
    const labelled = Boolean(title || props['aria-label'] || props['aria-labelledby']);
    return (
      <svg
        ref={ref} xmlns="http://www.w3.org/2000/svg" width={size} height={size}
        viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7}
        strokeLinecap="round" strokeLinejoin="round" focusable="false"
        role={labelled ? 'img' : undefined} aria-hidden={labelled ? undefined : true}
        aria-labelledby={title ? titleId : undefined}
        className={`cm-icon cm-icon-${name} ${className}`.trim()} {...props}
      >
        {title && <title id={titleId}>{title}</title>}
        {geometry}
        {children}
      </svg>
    );
  });
  Icon.displayName = `ChipIcon(${name})`;
  return Icon;
}

export type IconComponent = ReturnType<typeof createIcon>;

// Product icons share offset swatches, open corners and a single solid sample.
export const BrandMark = createIcon('brand', <>
  <path d="M14 3H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h1" />
  <rect x="9" y="7" width="12" height="14" rx="2" />
  <path d="M12 10h6v5h-6z" fill="currentColor" stroke="none" />
  <path d="M12 18h3" />
</>);
export const Palette = createIcon('palette', <>
  <rect x="3" y="4" width="18" height="16" rx="2" />
  <path d="M9 4v16m6-16v16" />
  <path d="M15 5h5v9h-5z" fill="currentColor" stroke="none" />
  <path d="M5.5 16.5h1m5-5h1" />
</>);
export const SwatchBook = createIcon('swatches', <>
  <rect x="3" y="3" width="11" height="15" rx="2" />
  <path d="M18 7h1a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-9" />
  <path d="M6 6h5v6H6z" fill="currentColor" stroke="none" />
  <path d="M6 15h2" />
</>);
export const CircleDot = createIcon('color-wheel', <>
  <path d="M12 3a9 9 0 1 0 9 9M12 3v5m-9 4h5m4 4v5" />
  <circle cx="12" cy="12" r="3" />
  <rect x="16" y="3" width="5" height="5" rx="1" fill="currentColor" stroke="none" />
</>);
export const ArrowLeftRight = createIcon('compare', <>
  <rect x="3" y="6" width="6" height="12" rx="1.5" />
  <rect x="15" y="6" width="6" height="12" rx="1.5" />
  <path d="M4 7h4v5H4z" fill="currentColor" stroke="none" />
  <path d="M11.5 8v1m0 3v1m0 3v1" />
</>);
export const ArrowRightLeft = createIcon('convert', <>
  <path d="M3 7h16m-3-3 3 3-3 3M21 17H5m3-3-3 3 3 3" />
</>);
export const Contrast = createIcon('contrast', <>
  <rect x="3" y="3" width="18" height="18" rx="3" />
  <path d="M12 3v18M12 4h6a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-6z" fill="currentColor" stroke="none" />
  <path d="M6 9v6" />
</>);
export const CheckCircle2 = createIcon('contrast-check', <>
  <path d="M20 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h8" />
  <path d="m10 8 4 4 7-8" />
  <path d="M6 15h3v3H6z" fill="currentColor" stroke="none" />
</>);
export const Layers = createIcon('layers', <>
  <rect x="3" y="3" width="12" height="10" rx="1.5" />
  <path d="M18 7v8a2 2 0 0 1-2 2H7m14-6v8a2 2 0 0 1-2 2h-8" />
  <path d="M6 6h6v4H6z" fill="currentColor" stroke="none" />
</>);
export const Suggestions = createIcon('suggestions', <>
  <rect x="3" y="4" width="7" height="16" rx="1.5" />
  <path d="M14 4h6v6h-6z" fill="currentColor" stroke="none" />
  <rect x="14" y="14" width="6" height="6" rx="1" />
  <path d="M6 15h1m-1 2h1" />
</>);

// Navigation and actions use the same grid, weight and compact open terminals.
export const ArrowRight = createIcon('arrow-right', <path d="M4 12h15m-6-6 6 6-6 6" />);
export const ArrowLeft = createIcon('arrow-left', <path d="M20 12H5m6-6-6 6 6 6" />);
export const ArrowDown = createIcon('arrow-down', <path d="M12 4v15m-6-6 6 6 6-6" />);
export const ChevronRight = createIcon('chevron-right', <path d="m9 6 6 6-6 6" />);
export const ChevronDown = createIcon('chevron-down', <path d="m6 9 6 6 6-6" />);
export const Menu = createIcon('menu', <><path d="M4 6h16M4 12h10M4 18h16" /><path d="M18 10h3v3h-3z" fill="currentColor" stroke="none" /></>);
export const X = createIcon('close', <path d="m6 6 12 12M18 6 6 18" />);
export const Plus = createIcon('plus', <path d="M5 12h14M12 5v14" />);
export const Minus = createIcon('minus', <path d="M5 12h14" />);
export const Check = createIcon('check', <path d="m4 12 5 5L20 6" />);
export const Copy = createIcon('copy', <>
  <path d="M7 16H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v2" />
  <path d="M10 8h7l4 4v7a2 2 0 0 1-2 2h-9a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2Zm7 0v4h4" />
</>);
export const Share2 = createIcon('share', <>
  <path d="M10 7h9v9m0-9-9 9M6 4H4v16h16v-2" />
  <path d="M3 3h4v4H3z" fill="currentColor" stroke="none" />
</>);
export const Code = createIcon('code', <><path d="m7 6-5 6 5 6m10-12 5 6-5 6M14 4l-4 16" /></>);
export const Code2 = Code;
export const RefreshCw = createIcon('refresh', <>
  <path d="M20 10a8 8 0 0 0-14-4L3 9m0-5v5h5m-4 5a8 8 0 0 0 14 4l3-3m-5 0h5v5" />
</>);
export const Shuffle = createIcon('shuffle', <>
  <path d="M3 6h3c5 0 7 12 12 12h3m-4-4 4 4-4 4M3 18h3c1.7 0 3-1.4 4.3-3.5m3.4-5C15 7.4 16.3 6 18 6h3m-4-4 4 4-4 4" />
</>);
export const History = createIcon('history', <>
  <path d="M4 9a8 8 0 1 1 0 6M3 4v5h5m4-3v6h4" />
</>);
export const Clock = createIcon('clock', <>
  <rect x="3" y="3" width="18" height="18" rx="5" /><path d="M12 6v6h5" />
</>);
export const Lock = createIcon('lock', <>
  <rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V6a4 4 0 0 1 8 0v4" />
  <path d="M10.5 14h3v3h-3z" fill="currentColor" stroke="none" />
</>);
export const Unlock = createIcon('unlock', <>
  <rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V6a4 4 0 0 1 8 0" />
  <path d="M10.5 14h3v3h-3z" fill="currentColor" stroke="none" />
</>);
export const GripVertical = createIcon('grip', <path d="M7 5h3v3H7zm7 0h3v3h-3zM7 11h3v3H7zm7 0h3v3h-3zM7 17h3v3H7zm7 0h3v3h-3z" fill="currentColor" stroke="none" />);
export const ToggleLeft = createIcon('toggle-off', <>
  <rect x="2" y="6" width="20" height="12" rx="3" /><rect x="5" y="9" width="6" height="6" rx="1" fill="currentColor" stroke="none" />
</>);
export const ToggleRight = createIcon('toggle-on', <>
  <rect x="2" y="6" width="20" height="12" rx="3" /><rect x="13" y="9" width="6" height="6" rx="1" fill="currentColor" stroke="none" />
</>);
export const Hash = createIcon('hex', <path d="M9 3 6 21M18 3l-3 18M3 8h18M2 16h18" />);
export const Pipette = createIcon('picker', <>
  <path d="m14 3 7 7m-4-4L5 18v3h3L20 9M10 13l3 3" />
  <path d="m16 2 6 6-3 3-6-6z" fill="currentColor" stroke="none" />
</>);
export const Upload = createIcon('upload', <>
  <path d="M12 16V3m-5 5 5-5 5 5M3 14v6h18v-6" /><path d="M6 17h3v1H6z" fill="currentColor" stroke="none" />
</>);
export const Wrench = createIcon('adjust', <>
  <path d="M4 7h16M4 17h16" /><rect x="7" y="4" width="4" height="6" rx="1" fill="currentColor" />
  <rect x="14" y="14" width="4" height="6" rx="1" fill="currentColor" />
</>);
export const Sliders = createIcon('sliders', <>
  <path d="M5 3v18M12 3v18M19 3v18" /><path d="M3 6h4v4H3zm7 8h4v4h-4zm7-8h4v4h-4z" fill="currentColor" stroke="none" />
</>);

// Content and preview surfaces are reduced to architectural frames.
export const BookOpen = createIcon('guide', <>
  <path d="M12 6c-3-2-6-2-9-2v15c3 0 6 0 9 2 3-2 6-2 9-2V4c-3 0-6 0-9 2Zm0 0v15M6 8h2m-2 4h2m8-4h2m-2 4h2" />
</>);
export const FileText = createIcon('article', <>
  <path d="M14 3H5v18h14V8Zm0 0v5h5M8 13h8m-8 4h5" /><path d="M8 6h3v3H8z" fill="currentColor" stroke="none" />
</>);
export const FileCheck = createIcon('review', <>
  <path d="M13 3H4v18h15v-6M13 3v6h6Z" /><path d="m11 14 3 3 7-7M7 7h2" />
</>);
export const Library = createIcon('library', <>
  <rect x="3" y="3" width="5" height="17" rx="1" /><path d="M12 4v16m4-16 4 16M3 15h5" /><path d="M3 4h5v5H3z" fill="currentColor" stroke="none" />
</>);
export const Layout = createIcon('layout', <>
  <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M10 9v12" /><path d="M5 5h3v2H5z" fill="currentColor" stroke="none" />
</>);
export const Monitor = createIcon('screen', <>
  <rect x="2" y="3" width="20" height="14" rx="2" /><path d="M9 17v4m6-4v4m-8 0h10" /><path d="M5 6h5v5H5z" fill="currentColor" stroke="none" />
</>);
export const Image = createIcon('image', <>
  <rect x="3" y="3" width="18" height="18" rx="2" /><path d="m3 17 6-6 5 5 3-3 4 4" />
  <rect x="14" y="6" width="4" height="4" rx="1" fill="currentColor" stroke="none" />
</>);
export const ShoppingBag = createIcon('shop', <>
  <path d="M4 7h16l1 14H3Zm4 0V6a4 4 0 0 1 8 0v1" /><path d="M8 11v1m8-1v1m-8 4h8" />
</>);
export const BarChart3 = createIcon('dashboard', <>
  <path d="M3 3v18h18M7 10v7m5-12v12" /><path d="M16 8h4v9h-4z" fill="currentColor" stroke="none" />
</>);
export const Printer = createIcon('print', <>
  <path d="M7 8V3h10v5M6 18H3V8h18v10h-3M7 14h10v7H7Z" /><path d="M16 10h3v2h-3z" fill="currentColor" stroke="none" />
</>);
export const Paintbrush = createIcon('paint', <>
  <path d="m14 3 7 7-8 8-7-7Zm-8 8-3 3 3 3-3 3 1 1 3-3 3 3 3-3" />
  <path d="m15 6 3 3-4 4-3-3z" fill="currentColor" stroke="none" />
</>);

// Environment and status symbols retain recognizable, accessible meanings.
export const Eye = createIcon('vision', <>
  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12Z" /><rect x="9" y="9" width="6" height="6" rx="2" fill="currentColor" stroke="none" />
</>);
export const Sun = createIcon('light', <>
  <circle cx="12" cy="12" r="4" /><path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1 1m12 12 1 1M5 19l1-1M18 6l1-1" />
</>);
export const Moon = createIcon('low-light', <>
  <path d="M11 3a9 9 0 1 0 10 10 8 8 0 0 1-10-10Z" /><rect x="16" y="4" width="4" height="4" rx="1" fill="currentColor" stroke="none" />
</>);
export const Glasses = createIcon('blur', <>
  <rect x="2" y="9" width="8" height="9" rx="3" /><rect x="14" y="9" width="8" height="9" rx="3" /><path d="M10 12h4M2 11l2-7h3m15 7-2-7h-3" />
</>);
export const Accessibility = createIcon('accessibility', <>
  <circle cx="12" cy="4" r="2" fill="currentColor" stroke="none" /><path d="m4 8 8 2 8-2m-8 2v5m0 0-5 6m5-6 5 6" />
</>);
export const Warning = createIcon('warning', <>
  <path d="m12 3 10 17H2Z" /><path d="M12 9v4" /><path d="M11 16h2v2h-2z" fill="currentColor" stroke="none" />
</>);
export const Grid3X3 = createIcon('matrix', <>
  <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18m6-18v18M3 9h18M3 15h18" /><path d="M10 10h4v4h-4z" fill="currentColor" stroke="none" />
</>);

// Harmony diagrams describe actual relationships rather than decorative symbols.
const harmonyRing = <circle cx="12" cy="12" r="9" strokeOpacity=".35" />;
export const Complementary = createIcon('complementary', <>{harmonyRing}<path d="M5 12h14" /><circle cx="5" cy="12" r="2.5" fill="currentColor" stroke="none" /><circle cx="19" cy="12" r="2.5" fill="currentColor" stroke="none" /></>);
export const Analogous = createIcon('analogous', <>{harmonyRing}<path d="M5.6 5.6A9 9 0 0 1 18.4 5.6" /><path d="M3.6 3.6h4v4h-4zm6.4-2.4h4v4h-4zm6.4 2.4h4v4h-4z" fill="currentColor" stroke="none" /></>);
export const Triadic = createIcon('triadic', <>{harmonyRing}<path d="m12 3 7.8 13.5H4.2Z" /><circle cx="12" cy="3" r="2.2" fill="currentColor" stroke="none" /><circle cx="19.8" cy="16.5" r="2.2" fill="currentColor" stroke="none" /><circle cx="4.2" cy="16.5" r="2.2" fill="currentColor" stroke="none" /></>);
export const SplitComplementary = createIcon('split-complementary', <>{harmonyRing}<path d="m12 3 4.5 16.8h-9Z" /><circle cx="12" cy="3" r="2.2" fill="currentColor" stroke="none" /><circle cx="7.5" cy="19.8" r="2.2" fill="currentColor" stroke="none" /><circle cx="16.5" cy="19.8" r="2.2" fill="currentColor" stroke="none" /></>);
export const Tetradic = createIcon('tetradic', <>{harmonyRing}<path d="M4.2 7.5h15.6v9H4.2Z" /><path d="M2.2 5.5h4v4h-4zm15.6 0h4v4h-4zm0 9h4v4h-4zm-15.6 0h4v4h-4z" fill="currentColor" stroke="none" /></>);
export const SquareHarmony = createIcon('square-harmony', <>{harmonyRing}<path d="m12 3 9 9-9 9-9-9Z" /><path d="M10 1h4v4h-4zm9 9h4v4h-4zm-9 9h4v4h-4zM1 10h4v4H1z" fill="currentColor" stroke="none" /></>);
export const Monochromatic = createIcon('monochromatic', <><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M9 4v16m6-16v16" /><path d="M4 5h5v14H4z" fill="currentColor" fillOpacity=".15" stroke="none" /><path d="M10 5h5v14h-5z" fill="currentColor" fillOpacity=".45" stroke="none" /><path d="M16 5h4v14h-4z" fill="currentColor" stroke="none" /></>);
